import { compose, mapObj, isEmpty, nonEmpty, pick } from './obj'

const sys = (s = {}) => s.sys || {}

const getFields = ({ fields = {} }) => fields

export const getAuthorName = (model = {}) => getFields(model).name || null
export const getAuthorUrl = (model = {}) => getFields(model).url || null
export const articleSchema = (page = {}) => getFields(page).articleSchema || null
export const contentId = (entity = {}) => sys(entity).id
export const spaceId = (entity = {}) => sys(entity).spaceId || sys(entity).space?.sys?.id
export const contentTypeId = (entity = {}) =>
  sys(entity)?.contentTypeId || sys(entity)?.contentType?.sys?.id

export const pageTitle = (page = {}) => getFields(page).title || ''

const contentfulEnv = process.env.CONTENTFUL_ENV || ''

export const dataProps = (entity = {}) =>
  nonEmpty({
    'data-content-type': contentTypeId(entity),
    'data-entity-id': contentId(entity),
    'data-space-id': spaceId(entity),
    'data-entity-name': entity.name || '',
    'data-entity-variant': entity.variant || '',
    'data-entity-layout': entity.layout || '',
    'data-environment': contentfulEnv,
  })

export const findInclude = (includes, id) =>
  Object.values(includes).find(it => contentTypeId(it) === id)

export const findIncludes = (includes, ids) =>
  Object.values(includes).filter(it => [].concat(ids).indexOf(contentTypeId(it)) > -1)

/**
 * A contentful Entity
 * @typedef {Object} Entity
 * @property {Object} sys - The System info object of the entity
 * @property {string} sys.id - The id of entity
 */

/**
 * findEntities
 * Given a list of root entities, will return them from the contentful
 * includes state.
 *
 * @param {Object<string, Object>} includes - The contentful includes map
 * @param {Entity[]} entities - List of contentful entities to find
 * @
 */
export const findEntities = (includes, entities) => {
  const entityIds = entities.map(contentId)
  return Object.values(pick(entityIds, includes))
}

// Selectors
const file = (s = {}) => s.file || {}

const sysId = (s = {}) => sys(s).id || ''

export const fileType = (entity = {}) => file(entity).contentType || ''

// Modifiers
export const assignContentTypeId = id => ({ sys, ...entity }) => ({
  ...entity,
  sys: { ...sys, contentTypeId: id },
})

/* Simpify entities */
const simplifyEntitySys = sys =>
  nonEmpty({
    id: sys?.id,
    spaceId: sys?.space?.sys?.id,
    contentTypeId: sys?.contentType?.sys?.id,
    total: sys?.total,
    createdAt: sys?.createdAt,
    updatedAt: sys?.updatedAt,
    trimmedContent: sys?.trimmedContent,
  })

const simplifyEntrySys = entry => (entry?.sys?.id ? { sys: { id: entry?.sys?.id } } : undefined)

const simplifyEntityFields = fields =>
  nonEmpty(
    !Array.isArray(fields?.entries)
      ? fields
      : {
          ...fields,
          entries: fields.entries.map(simplifyEntrySys).filter(i => i),
        },
  )

const simplifyEntity = entity => {
  if (!entity || isEmpty(entity)) return {}

  const { fields, sys } = entity
  return nonEmpty(
    {
      fields: simplifyEntityFields(fields),
      sys: simplifyEntitySys(sys),
    },
    {},
  )
}

const simplifyEntities = ({ includes = {}, rootEntity = {}, rootEntities = [], ...rest }) => ({
  ...rest,
  rootEntity: simplifyEntity(rootEntity),
  rootEntities: rootEntities.map(simplifyEntity),
  includes: mapObj(simplifyEntity, includes),
})

/* Simplify Product dataset */
const simplifyProductDatasets = ({ includes = {}, ...rest }) => {
  const productTables = Object.values(includes).filter(it => contentTypeId(it) === 'productTable')

  const productDatasets = productTables
    .map(({ fields }) => [fields, includes[sysId(fields.dataset)]])
    .reduce((acc, [{ rowCount, filterIds }, entity = {}]) => {
      if (entity?.fields?.query) return entity
      const data = (entity?.fields?.data || [])
        .filter(({ id }) => (!filterIds ? true : filterIds.includes(id)))
        .slice(0, rowCount)
      return {
        ...acc,
        [sysId(entity)]: {
          ...entity,
          fields: { ...entity.fields, data },
        },
      }
    }, {})

  return { ...rest, includes: { ...includes, ...productDatasets } }
}

export const simplifyCtx = compose(simplifyProductDatasets, simplifyEntities)
